import api from './axios';

/**
 * Create a Production Lot along with nested tasks, confirmation groups, materials, and assign tasks to users/teams
 * @param {Object} request - Production lot creation request
 * @param {Object} request.lot - Production lot data
 * @param {Array} request.taskAssignments - Task assignments (optional)
 */
export const createProductionLot = async (payload) => {
  try {
    const response = await api.post('/api/production-tasks', payload);

    // Check if the response indicates a duplicate even though it's 200
    if (response.data && response.data.message &&
      response.data.message.includes('already exists')) {
      // This is a duplicate record disguised as success
      const error = new Error(response.data.message);
      error.isDuplicate = true;
      error.response = { data: response.data };
      throw error;
    }

    return response.data;
  } catch (error) {
    // If using axios, error.response contains the server response
    if (error.response) {
      // Extract the error message from the response
      const serverMessage = error.response.data?.message ||
        error.response.data?.error ||
        error.response.data?.title ||
        (typeof error.response.data === 'string' ? error.response.data : null);

      if (serverMessage) {
        // Create a new error with the server message
        const enhancedError = new Error(serverMessage);
        enhancedError.response = error.response;
        enhancedError.isDuplicate = serverMessage.includes('already exists');
        throw enhancedError;
      } else if (error.response.data) {
        // If we have data but no message field, stringify it
        const enhancedError = new Error(JSON.stringify(error.response.data));
        enhancedError.response = error.response;
        throw enhancedError;
      }
    }

    // Check if it's our custom duplicate error
    if (error.isDuplicate) {
      throw error;
    }

    // If no specific error message, throw the original error
    throw error;
  }
};

/**
 * Get all production tasks with pagination
 * @param {Object} params - Query parameters
 * @param {number} params.pageNumber - Page number (default: 1)
 * @param {number} params.pageSize - Page size (default: 10)
 */
export const getProductionTasks = async (params = {}) => {
  try {
    const { pageNumber = 1, pageSize = 10 } = params;
    const response = await api.get('/api/production-tasks', {
      params: { pageNumber, pageSize }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching production tasks:', error);
    throw error;
  }
};

/**
 * Get a specific production task by ID
 * @param {string} id - Task ID
 */
export const getProductionTaskById = async (id) => {
  try {
    const response = await api.get(`/api/production-tasks/${encodeURIComponent(id)}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching production task:', error);
    throw error;
  }
};

/**
 * Lightweight count of production tasks visible to the current user.
 * GET /api/production-tasks/count
 * Returns { isSuccess: true, data: number }
 */
export const getProductionTaskCount = async () => {
  try {
    const response = await api.get('/api/production-tasks/count');
    return response.data;
  } catch (error) {
    console.error('Error fetching production task count:', error);
    return { isSuccess: false, data: 0 };
  }
};

/**
 * Lightweight lot summaries for the Task Picker (no ConfirmationGroups / Activities / Materials).
 * GET /api/production-tasks/summaries
 */
export const getProductionLotSummaries = async (params = {}) => {
  try {
    const {
      pageNumber = 1,
      pageSize = 100,
      productionTaskId,
      productionLotId,
      salesOrderNo,
      Status,
      searchTerm
    } = params;

    const response = await api.get('/api/production-tasks/summaries', {
      params: {
        pageNumber,
        pageSize,
        ...(productionTaskId && { productionTaskId }),
        ...(productionLotId && { productionLotId }),
        ...(salesOrderNo && { salesOrderNo }),
        ...(Status && { Status }),
        ...(searchTerm && { searchTerm })
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching production lot summaries:', error);
    throw error;
  }
};

/**
 * Get all full production lots with pagination
 * Filtering is done server-side based on JWT claims (userId + role)
 * @param {Object} params - Query parameters
 * @param {number} params.pageNumber - Page number (default: 1)
 * @param {number} params.pageSize - Page size (default: 10)
 */
export const getAllFullProductionLots = async (params = {}) => {
  try {
    const { pageNumber = 1, pageSize = 10 } = params;
    const response = await api.get('/api/production-tasks/full', {
      params: { pageNumber, pageSize }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching all full production lots:', error);
    throw error;
  }
};

/**
 * Get full Production Lot by ProductionLotId including all nested objects and task assignments
 * @param {string} productionLotId - Production Lot ID
 */
export const getFullProductionLot = async (productionLotId) => {
  try {
    const response = await api.get(`/api/production-tasks/${encodeURIComponent(productionLotId)}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching full production lot:', error);
    throw error;
  }
};

export const updateTaskAssignments = async (payload) => {
  try {
    const response = await api.post('/api/production-tasks/assignments', payload);
    return response.data;
  } catch (error) {
    console.error('Error updating task assignments:', error);

    if (error.response) {
      const serverMessage =
        error.response.data?.message ||
        error.response.data?.error ||
        error.response.data?.title ||
        (typeof error.response.data === 'string'
          ? error.response.data
          : 'Failed to update assignments');

      const enhancedError = new Error(serverMessage);
      enhancedError.response = error.response;
      throw enhancedError;
    }

    throw error;
  }
};

/**
 * Get tasks assigned to a specific user
 * @param {string} userId - User ID
 */
export const getTasksByUser = async (userId) => {
  try {
    const response = await api.get(`/api/production-tasks/user/${encodeURIComponent(userId)}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching tasks by user:', error);
    throw error;
  }
};

/**
 * Get tasks assigned to a specific team
 * @param {string} teamId - Team ID
 */
export const getTasksByTeam = async (teamId) => {
  try {
    const response = await api.get(`/api/production-tasks/team/${encodeURIComponent(teamId)}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching tasks by team:', error);
    throw error;
  }
};

/**
 * Start a production task — sets its ProcessingStatusCode to "2" (In Process) in the DB.
 * Call this when the user confirms they want to move a Pending task to In Process.
 * @param {string} productionTaskId - The SAP business ID of the production task (e.g. "23682")
 * @returns {Promise<{isSuccess: boolean, message: string, data: object}>}
 */
export const startProductionTask = async (productionTaskId) => {
  try {
    const response = await api.post(`/api/production-tasks/start/${encodeURIComponent(productionTaskId)}`);
    return response.data;
  } catch (error) {
    console.error('Error starting production task:', error);
    if (error.response) {
      const serverMessage =
        error.response.data?.message ||
        error.response.data?.error ||
        error.response.data?.title ||
        'Failed to start production task';
      const enhancedError = new Error(serverMessage);
      enhancedError.response = error.response;
      throw enhancedError;
    }
    throw error;
  }
};

/**
 * Complete a production task activity — Step 3 Final Confirmation.
 * Sends a final activity confirmation to SAP with ConfirmationCompletedIndicator=true
 * for the specified activity only. If all activities in the group are completed,
 * also finishes the entire task.
 * @param {string} productionTaskId - The SAP business ID of the production task
 * @param {string|null} activityId - Optional: The specific activity ID to complete
 * @returns {Promise<{isSuccess: boolean, message: string, data: object}>}
 */
export const completeProductionTask = async (productionTaskId, activityId = null) => {
  try {
    const params = activityId ? `?activityId=${encodeURIComponent(activityId)}` : '';
    const response = await api.post(`/api/production-tasks/complete/${encodeURIComponent(productionTaskId)}${params}`);
    return response.data;
  } catch (error) {
    console.error('Error completing production task:', error);
    if (error.response) {
      const serverMessage =
        error.response.data?.message ||
        error.response.data?.error ||
        error.response.data?.title ||
        'Failed to complete production task';
      const enhancedError = new Error(serverMessage);
      enhancedError.response = error.response;
      throw enhancedError;
    }
    throw error;
  }
};

/**
 * Sync production tasks from SAP for the current user.
 * Queries SAP by ResponsibleEmployeeID (user's SAP Employee ID),
 * fetches full lot data, and imports any not already in the database.
 * POST /api/production-tasks/sync-from-sap
 * @returns {Promise<{isSuccess: boolean, message: string, data: SyncFromSapResult}>}
 */
export const syncProductionTasksFromSap = async () => {
  try {
    const response = await api.post('/api/production-tasks/sync-from-sap');
    return response.data;
  } catch (error) {
    console.error('Error syncing production tasks from SAP:', error);
    if (error.response) {
      const serverMessage =
        error.response.data?.message ||
        error.response.data?.error ||
        error.response.data?.title ||
        'Failed to sync production tasks from SAP';
      const enhancedError = new Error(serverMessage);
      enhancedError.response = error.response;
      throw enhancedError;
    }
    throw error;
  }
};

export default {
  createProductionLot,
  getProductionTasks,
  getProductionTaskById,
  getProductionTaskCount,
  getProductionLotSummaries,
  getAllFullProductionLots,
  getFullProductionLot,
  getTasksByUser,
  getTasksByTeam,
  updateTaskAssignments,
  startProductionTask,
  completeProductionTask,
  syncProductionTasksFromSap
};